import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { ApiError, api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { tt } from '@/internationalization/i18n'
import { toast, useUiStore } from '@/stores'
import type {
  Billing,
  LanguageSettings,
  NotificationSettings,
  ProfileSettings,
  RecordingSettings,
  SecuritySettings,
  Tenant,
  User,
} from '@/types'
import { settingsApi } from './api'

/**
 * Settings panes all follow the same shape: one query for the current values,
 * one mutation that PATCHes a partial and invalidates. `useSettingsSection`
 * builds both so a new pane is three lines rather than thirty, and so every
 * pane gets the same success toast and the same error handling.
 */
function useSettingsSection<T>(
  key: QueryKey,
  read: () => Promise<T>,
  write: (patch: Partial<T>) => Promise<T>,
  // Gates the FETCH, not the render. A pane can be visible to somebody who may
  // not read one of its sections, and asking anyway is a 403 in the console on
  // every visit.
  enabled = true,
) {
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: key, queryFn: read, enabled })
  const save = useMutation({
    mutationFn: write,
    onSuccess: (next) => {
      queryClient.setQueryData(key, next)
      toast.success(tt('common.saved'))
    },
    onError: () => toast.error(tt('common.couldNotSave'), tt('common.couldNotSaveBody')),
  })
  return { query, save }
}

export const useProfileSettings = () =>
  useSettingsSection<ProfileSettings>(qk.settings.profile, settingsApi.profile.get, settingsApi.profile.update)

/**
 * The personal-email toggles apply immediately, unlike the rest of the profile
 * form which waits for Save changes -- so they reuse the same PATCH endpoint
 * but skip the generic "Saved" toast that would otherwise fire on every flip.
 */
export function useUpdateProfilePreference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: settingsApi.profile.update,
    onSuccess: (next) => queryClient.setQueryData(qk.settings.profile, next),
    onError: () => toast.error(tt('common.couldNotSave'), tt('common.couldNotSaveBody')),
  })
}

/**
 * Applies the saved interface language across the app.
 *
 * The preference is persisted server-side on the profile, so it survives
 * refresh and re-login; this reads it on every app load (mounted once by
 * AppShell) and pushes it into `useUiStore`, which the UI renders from via
 * `useT`. Save changes on the Profile pane writes the same query cache, so
 * switching language there applies without a reload.
 */
export function useApplyInterfaceLanguage() {
  const { query } = useProfileSettings()
  const setUiLanguage = useUiStore((s) => s.setUiLanguage)
  const language = query.data?.interfaceLanguage
  useEffect(() => {
    if (language) setUiLanguage(language)
  }, [language, setUiLanguage])
}

export const useWorkspaceSettings = () =>
  useSettingsSection<Tenant>(qk.settings.workspace, settingsApi.workspace.get, settingsApi.workspace.update)

export const useLanguageSettings = () =>
  useSettingsSection<LanguageSettings>(qk.settings.languages, settingsApi.languages.get, settingsApi.languages.update)

export const useNotificationSettings = () =>
  useSettingsSection<NotificationSettings>(
    qk.settings.notifications,
    settingsApi.notifications.get,
    settingsApi.notifications.update,
  )

/**
 * The workspace's screen-capture default.
 *
 * `enabled` gates the FETCH, not just the render: the endpoint is behind
 * `meeting:screen_capture`, so asking without it is a 403 in the console on
 * every visit to a pane the person is otherwise allowed to see.
 */
export const useRecordingSettings = (enabled = true) =>
  useSettingsSection<RecordingSettings>(
    qk.settings.recording,
    settingsApi.recording.get,
    settingsApi.recording.update,
    enabled,
  )

export const useSecuritySettings = () =>
  useSettingsSection<SecuritySettings>(qk.settings.security, settingsApi.security.get, settingsApi.security.update)

export function useMembers() {
  return useQuery({ queryKey: qk.settings.members, queryFn: settingsApi.members.list })
}

/**
 * Change a member's role, or suspend them.
 *
 * The refusals here are all rules a person can act on -- "only somebody who
 * holds that role may grant it", "you cannot change your own role" -- so the
 * server's own sentence is shown. Collapsing them into "could not save" would
 * hide the only explanation of the protection rule that exists at the moment
 * somebody hits it.
 */
export function useUpdateMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { role?: string; status?: User['status'] } }) =>
      settingsApi.members.update(id, patch as Parameters<typeof settingsApi.members.update>[1]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.settings.members })
      // A role change alters what that person may see, and if it was your own
      // membership the API has already refused -- but an ADMIN changing their
      // colleague still changes the role's member count on the roles screen.
      queryClient.invalidateQueries({ queryKey: qk.authorization.all })
      toast.success(tt('members.toast.updated'))
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status < 500) {
        return toast.error(tt('members.toast.updateFail'), error.message)
      }
      toast.error(tt('members.toast.updateFail'), tt('common.somethingWentWrong'))
    },
  })
}

export function useBilling() {
  return useQuery<Billing>({ queryKey: qk.settings.billing, queryFn: settingsApi.billing })
}

export function useRecordingRules() {
  return useQuery({ queryKey: qk.settings.rules, queryFn: settingsApi.rules.list })
}

export function useToggleRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      settingsApi.rules.update(id, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.settings.rules }),
    onError: () => toast.error(tt('settings.toast.couldNotUpdateRule')),
  })
}

export function useInviteMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: settingsApi.members.invite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.settings.members })
      toast.success(tt('settings.toast.invitationSent'))
    },
    // A 409 (already a member, no seats available) or 403 (role is protected)
    // is user-correctable and names the reason -- same convention as
    // `useUpdateMember` -- so the server's own sentence is shown rather than a
    // generic failure that hides why the invite was refused.
    onError: (error) => {
      if (error instanceof ApiError && isInvitationReportable(error)) {
        return toast.error(tt('settings.toast.couldNotSendInvitation'), error.message)
      }
      toast.error(tt('settings.toast.couldNotSendInvitation'))
    },
  })
}

/**
 * The 5xx codes the server raises on purpose for a created-but-unsent
 * invitation. Their message is written for the admin and carries no Graph
 * detail, so it is shown; any other 5xx stays generic.
 */
const INVITATION_EMAIL_CODES = new Set(['invitation_email_failed', 'invitation_email_not_configured'])

export function isInvitationReportable(error: ApiError): boolean {
  return error.status < 500 || INVITATION_EMAIL_CODES.has(error.code)
}

export function useRequestExport() {
  return useMutation({
    mutationFn: settingsApi.requestExport,
    onSuccess: () => toast.info(tt('settings.toast.exportQueued')),
    onError: () => toast.error(tt('settings.toast.couldNotQueueExport')),
  })
}

/**
 * Workspaces this user belongs to. Powers the tenant switcher.
 *
 * Long stale time: memberships change on the scale of weeks, and this fires on
 * every app load.
 */
export function useMemberships() {
  return useQuery({
    queryKey: qk.memberships,
    queryFn: () => api.get<Tenant[]>('/memberships'),
    staleTime: 5 * 60_000,
  })
}
